import React from "react";
import { styled } from "@mui/system";

interface FilterButtonProps {
  select: boolean;
}
const FilterButton = styled("button")<FilterButtonProps>(({ select }) => ({
  display: "flex",
  width: "100%",
  padding: "4px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  borderRadius: "4px",
  background: select ? "#F5F3EF" : "#F8F9F9",
  border: "1px solid transparent",
  cursor: "pointer",

  // hover styles for FilterButton
  "&:hover": {
    background: select ? "#F5F3EF" : "#FCFCFB",
    border: select ? "1px solid transparent" : "1px solid #F5F3EF",
  },

  // hover styles for IconWrapper
  "&:hover > div:first-of-type": {
    background: select ? "#E5E1D7" : "#FCFCFB",
  },

  // active styles for IconWrapper
  "&:active > div:first-of-type": {
    background: select ? "#E5E1D7" : "#FCFCFB",
  },
}));

const IconWrapper = styled("div")<FilterButtonProps>(({ select }) => ({
  display: "flex",
  padding: "4px",
  justifyContent: "center",
  alignItems: "center",
  gap: "4px",
  borderRadius: "8px",
  background: select ? "#E5E1D7" : "#F8F9F9",
}));

const TextWrapper = styled("div")(() => ({
  color: "#002035",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "17px",
}));

interface NotificationCircleProps {
  show: boolean;
}
const NotificationCircleWrapper = styled("div")<NotificationCircleProps>(({ show }) => ({
  position: "relative",
  top: 0,
  left: 0,
  visibility: show ? "visible" : "hidden",
}));

const NotificationCircle = styled("div")(() => ({
  width: "4px",
  height: "4px",
  position: "absolute",
  right: "95px",
  bottom: "2px",
  borderRadius: "8.889px",
  background: "#FC4C4C",
}));

interface FilterTabButtonProps {
  text: string;
  icon: JSX.Element;
  selected?: boolean;
  notification?: boolean;
  onClick?: () => void;
}

/**
 * A button component for filtering options.
 * @param {string} props.text - The text content of the button.
 * @param {JSX.Element} props.icon - The icon element to display within the button.
 * @param {boolean} [props.selected=false] - Whether the button is selected (default is false).
 * @param {boolean} [props.notification=false] - Whether to display a notification indicator (default is false).
 * @param {function} [props.onClick] - Function to run when user clicks on button (optional).
 */
const FilterTabButton = (props: FilterTabButtonProps) => {
  const { text, icon, selected, notification, onClick } = props;

  return (
    <FilterButton select={selected ?? false} onClick={onClick}>
      <IconWrapper select={selected ?? false}>{icon}</IconWrapper>
      <TextWrapper>{text}</TextWrapper>

      {/* place at end to avoid shifting button layout */}
      <NotificationCircleWrapper show={notification ?? false}>
        <NotificationCircle />
      </NotificationCircleWrapper>
    </FilterButton>
  );
};

export default FilterTabButton;
